import React from 'react';
import { Box, Typography } from '@mui/material';
import { DraggableProvided, DraggableStateSnapshot } from '@hello-pangea/dnd';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ConditionTree from './ConditionTree';
import { ConditionGroupType, IConditionNode, IRulesetSchemaField } from '../../../types/ruleset-types';
import { GroupConfig } from './ConditionGroupHeader';

interface ConditionListItemProps {
  child: IConditionNode;
  index: number;
  groupType: ConditionGroupType;
  config: GroupConfig;
  provided: DraggableProvided;
  snapshot: DraggableStateSnapshot;
  schema: { fields: IRulesetSchemaField[] };
  depth: number;
  dataPoolId: string;
  disabled: boolean;
  insideNot: boolean;
  onChildChange: (index: number, child: IConditionNode) => void;
  onChildRemove: (index: number) => void;
}

const ConditionListItem: React.FC<ConditionListItemProps> = ({
  child,
  index,
  groupType,
  config,
  provided,
  snapshot,
  schema,
  depth,
  dataPoolId,
  disabled,
  insideNot,
  onChildChange,
  onChildRemove,
}) => {
  return (
    <Box ref={provided.innerRef} {...provided.draggableProps}>
      {/* Divider between conditions */}
      {index > 0 && groupType !== 'not' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
          <Box sx={{ flex: 1, height: '1px', backgroundColor: 'divider' }} />
          <Typography
            variant="caption"
            sx={{
              color: config.color,
              fontWeight: 600,
              textTransform: 'uppercase',
              fontSize: '10px',
              letterSpacing: '0.5px',
            }}
          >
            {groupType.toUpperCase()}
          </Typography>
          <Box sx={{ flex: 1, height: '1px', backgroundColor: 'divider' }} />
        </Box>
      )}

      {/* Condition row with drag handle */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          backgroundColor: snapshot.isDragging ? 'action.hover' : 'transparent',
          borderRadius: 1,
          minWidth: 0,
        }}
      >
        {/* Drag handle */}
        {groupType !== 'not' && (
          <Box
            {...provided.dragHandleProps}
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 0.5,
              color: 'text.disabled',
              cursor: disabled ? 'not-allowed' : 'grab',
              '&:hover': { color: 'text.secondary' },
            }}
          >
            <DragIndicatorIcon sx={{ fontSize: 18 }} />
          </Box>
        )}

        {/* Condition content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <ConditionTree
            node={child}
            schema={schema}
            depth={depth + 1}
            onChange={(updated) => onChildChange(index, updated)}
            onRemove={() => onChildRemove(index)}
            dataPoolId={dataPoolId}
            disabled={disabled}
            insideNot={insideNot}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default ConditionListItem;
